import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import { createAlgoliaInsightsPlugin } from '@algolia/autocomplete-plugin-algolia-insights';
import { liteClient } from 'algoliasearch/lite';
import aa from 'search-insights';

interface IndexDefinition {
  code: string;
  fullIndexName: string;
  displayName: string;
  /** Legacy server field; layout uses one column per index regardless. */
  column?: string;
}

interface ClientConfig {
  applicationId: string;
  searchApiKey: string;
  resultsUrl: string;
  indexName: string;
  indexQuerySuggestionName: string;
  indexDefinitions: IndexDefinition[];
  isInsightEnabled: boolean;
  isEmptySearchesEnabled: boolean;
  minCharactersForAutocompleteRequest: number;
}

function readConfig(): ClientConfig | null {
  const el = document.getElementById('algolia-searchbox-config');
  const raw = el?.textContent?.trim();
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ClientConfig;
  } catch {
    return null;
  }
}

function hitTitle(hit: Record<string, unknown>): string {
  const title = hit.Title ?? hit.title ?? hit.name ?? hit.Name;
  if (typeof title === 'string') return title;
  return String(hit.objectID ?? '');
}

function hitUrl(hit: Record<string, unknown>): string {
  const u = hit.url ?? hit.Url ?? hit.URL;
  if (typeof u === 'string' && u.length) return u;
  return '#';
}

function hitImageUrl(hit: Record<string, unknown>): string {
  const candidates = [hit.imageUrl, hit.ImageUrl, hit.image, hit.Image, hit.thumbnailUrl, hit.ThumbnailUrl];
  for (const c of candidates) {
    if (typeof c === 'string' && c.length) return c;
  }
  return '';
}

const PRODUCTS_SOURCE_ID = 'Products';
const SERIES_SOURCE_ID = 'Series';
const SERIES_TAG = 'is_series';
const SERIES_FILTER = `_tags:${SERIES_TAG}`;
const PARTS_FILTER = `NOT _tags:${SERIES_TAG}`;

function isProductLikeSource(sourceId: string): boolean {
  return sourceId === PRODUCTS_SOURCE_ID || sourceId === SERIES_SOURCE_ID;
}

function productLikeColumnTitle(sourceId: string): string {
  return sourceId === SERIES_SOURCE_ID ? 'Top Matching Series' : 'Top Matching Parts';
}

function queryMeetsMinLength(query: string, minChars: number): boolean {
  return query.trim().length >= minChars;
}

function getCollectionItemCount(collections: { items?: unknown[] }[]): number {
  return collections.reduce((sum, collection) => sum + (collection.items?.length ?? 0), 0);
}

function navigateToResults(resultsUrl: string, query: string): void {
  const q = query.trim();
  if (!q || !resultsUrl) return;
  const sep = resultsUrl.includes('?') ? '&' : '?';
  window.location.href = `${resultsUrl}${sep}q=${encodeURIComponent(q)}`;
}

function initAlgoliaSearchbox(): void {
  const box = document.getElementById('algolia-search-box');
  const mount = document.getElementById('algolia-autocomplete-root');
  if (!box || !mount) return;

  const config = readConfig();
  if (!config?.applicationId || !config.searchApiKey) return;
  if (!config.indexDefinitions?.length) return;

  const searchClient = liteClient(config.applicationId, config.searchApiKey);

  const minChars = Math.max(1, config.minCharactersForAutocompleteRequest ?? 3);

  const plugins = config.isInsightEnabled
    ? [
      createAlgoliaInsightsPlugin({
        insightsClient: aa,
        // search-insights InitParams typing differs from Algolia dashboard field names in some TS versions
        insightsInitParams: {
          appId: config.applicationId,
          apiKey: config.searchApiKey,
          useCookie: true,
        } as never,
      }),
    ]
    : [];

  autocomplete({
    container: mount,
    placeholder: 'Search',
    openOnFocus: false,
    shouldPanelOpen({ state }) {
      if (!queryMeetsMinLength(state.query, minChars)) {
        return false;
      }
      return getCollectionItemCount(state.collections) > 0;
    },
    plugins,
    detachedMediaQuery: 'none',
    panelContainer: mount,
    onSubmit: (params) => navigateToResults(config.resultsUrl, params.state.query),
    getSources: () => {
      const buildSource = (
        sourceId: string,
        indexName: string,
        hitsPerPage: number,
        filters: string | undefined,
        headerLabel: string,
      ) => ({
        sourceId,
        getItems({ query }: { query: string }) {
          if (!queryMeetsMinLength(query, minChars)) {
            return [];
          }
          const q = query.trim();
          const params: Record<string, unknown> = { hitsPerPage };
          if (filters) params.filters = filters;
          return getAlgoliaResults<Record<string, unknown>>({
            searchClient: searchClient as Parameters<typeof getAlgoliaResults>[0]['searchClient'],
            queries: [
              {
                indexName,
                query: q,
                params,
              } as Parameters<typeof getAlgoliaResults>[0]['queries'][number],
            ],
          });
        },
        templates: {
          header({ html }: { html: (strings: TemplateStringsArray, ...values: unknown[]) => unknown }) {
            return html`<div class="algolia-autocomplete__source-header">${headerLabel}</div>`;
          },
          item({
            item,
            html,
          }: {
            item: unknown;
            html: (strings: TemplateStringsArray, ...values: unknown[]) => unknown;
          }) {
            const hit = item as Record<string, unknown>;
            const title = hitTitle(hit);
            const url = hitUrl(hit);
            return html`<a
              class="aa-ItemLink algolia-autocomplete__hit"
              href="${url}"
              >${title}</a
            >`;
          },
        },
      });

      const sources = config.indexDefinitions.flatMap((def) => {
        if (def.code === PRODUCTS_SOURCE_ID) {
          return [
            buildSource(SERIES_SOURCE_ID, def.fullIndexName, 3, SERIES_FILTER, 'Top Matching Series'),
            buildSource(PRODUCTS_SOURCE_ID, def.fullIndexName, 3, PARTS_FILTER, 'Top Matching Parts'),
          ];
        }
        return [buildSource(def.code, def.fullIndexName, 3, undefined, `Top Matching ${def.displayName}`)];
      });
      return sources as unknown as ReturnType<
        Exclude<Parameters<typeof autocomplete>[0]['getSources'], undefined>
      >;
    },
    render(params, root) {
      const { sections, state, html, render: renderVnode, children } = params;

      if (!queryMeetsMinLength(state.query, minChars)) {
        renderVnode(children, root);
        return;
      }

      if (!sections.length) {
        renderVnode(children, root);
        return;
      }

      const renderProductColumn = (title: string, items: Record<string, unknown>[]) => {
        const productBase = config.resultsUrl.replace('/search', '').replace(/\/+$/, '');
        const itemNodes = items.map((hit) => {
          const displayTitle = hitTitle(hit);
          const path = String(hit.url ?? hit.Url ?? hit.URL ?? '').replace(/^\/+/, '');
          const url = path.length ? `${productBase}/${path}` : '#';
          const showImg = Boolean(hit.hasImage) && hitImageUrl(hit).length > 0;
          const imgSrc = showImg ? hitImageUrl(hit) : '';
          return html`<a
            class="aa-ItemLink algolia-autocomplete__hit algolia-autocomplete__hit--product"
            href="${url}"
            >${showImg
              ? html`<img
                  class="algolia-autocomplete__hit-img"
                  src="${imgSrc}"
                  width="80"
                  height="80"
                  alt=""
                  loading="lazy"
                />`
              : html`<div class="algolia-autocomplete__hit-img" aria-hidden="true"></diu>`} <span>${displayTitle}</span></a
          >`;
        });
        return html`<div
          class="algolia-autocomplete__column"
          role="region"
          aria-label="${title}"
        >
          <div class="algolia-autocomplete__source-header">${title}</div>
          ${itemNodes}
        </div>`;
      };

      const renderGenericColumn = (title: string, items: Record<string, unknown>[]) => {
        const itemNodes = items.map((hit) => {
          const displayTitle = hitTitle(hit);
          const url = hitUrl(hit);
          return html`<a class="aa-ItemLink algolia-autocomplete__hit" href="${url}">${displayTitle}</a>`;
        });
        return html`<div
          class="algolia-autocomplete__column"
          role="region"
          aria-label="${title}"
        >
          <div class="algolia-autocomplete__source-header">${title}</div>
          ${itemNodes}
        </div>`;
      };

      const columnNodes: Array<ReturnType<typeof renderProductColumn>> = [];

      state.collections.forEach((collection) => {
        const sourceId = collection.source.sourceId;
        const def = config.indexDefinitions.find((d) => d.code === sourceId);
        const items = (collection.items ?? []) as Record<string, unknown>[];

        if (isProductLikeSource(sourceId)) {
          if (items.length) {
            columnNodes.push(renderProductColumn(productLikeColumnTitle(sourceId), items.slice(0, 3)));
          }
        } else if (items.length) {
          const title = def?.displayName ? `Top Matching ${def.displayName}` : sourceId;
          columnNodes.push(renderGenericColumn(title, items.slice(0, 3)));
        }
      });

      // Sort columns: Resources, Series, Pages, Parts
      columnNodes.sort((a, b) => {
        const getColumnType = (node: ReturnType<typeof renderProductColumn>): string => {
          const label = (node as any)?.props?.['aria-label'] || '';

          if (label.includes('Top Matching Series') || label === 'Series') return 'Series';
          if (label.includes('Top Matching Parts') || label === 'Parts') return 'Parts';
          if (label.includes('Resources')) return 'Resources';
          if (label.includes('Pages')) return 'Pages';
          return 'Other';
        };

        const order = ['Resources', 'Series', 'Pages', 'Parts', 'Other'];
        const typeA = getColumnType(a);
        const typeB = getColumnType(b);
        return order.indexOf(typeA) - order.indexOf(typeB);
      });

      if (!columnNodes.length) {
        renderVnode(children, root);
        return;
      }

      const panel = html`<div class="algolia-autocomplete__columns">${columnNodes}</div>`;
      renderVnode(panel as never, root);
    },
  });

  // Algolia renders the submit button inside `.aa-InputWrapperPrefix` before the
  // input in the DOM, causing screen readers and keyboard focus to land on the
  // submit button first. Move the input wrapper to precede the prefix so DOM
  // order matches the intended tab/read order: input → submit → clear.
  const form = mount.querySelector<HTMLElement>('.aa-Form');
  if (form) {
    const prefix = form.querySelector<HTMLElement>('.aa-InputWrapperPrefix');
    const inputWrapper = form.querySelector<HTMLElement>('.aa-InputWrapper');
    if (prefix && inputWrapper) {
      form.insertBefore(inputWrapper, prefix);
    }
  }
}

initAlgoliaSearchbox();
